import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { DailySchedule } from '@/schedule/DailySchedule.atom';
import { MonthlySchedule } from '@/schedule/MonthlySchedule.atom';
import { WeeklySchedule } from '@/schedule/WeeklySchedule.atom';
import { ScheduleTypeName } from '@/schedule/schedule.types';
import { Select } from '@seeqdev/qomponents';
import type {
  DailyScheduleType,
  MonthlyScheduleData,
  ScheduleType,
  WeeklyScheduleData,
} from '@/schedule/schedule.types';

interface ScheduleTypeProps {
  scheduleType: ScheduleType;
  setScheduleType: (scheduleType: ScheduleType) => void;
  helpContent?: React.ReactNode;
  scheduleTypeTranslations: ScheduleTypeTranslations;
  allowedScheduleTypes?: ScheduleTypeName[];
  disabled?: boolean;
}

const TRANSLATION_FOR_SCHEDULE_TYPE = {
  [ScheduleTypeName.DAILY]: 'SCHEDULE_TYPE.DAILY.NAME',
  [ScheduleTypeName.WEEKLY]: 'SCHEDULE_TYPE.WEEKLY.NAME',
  [ScheduleTypeName.MONTHLY]: 'SCHEDULE_TYPE.MONTHLY.NAME',
  [ScheduleTypeName.LIVE]: 'SCHEDULE_TYPE.LIVE.NAME',
};

export type ScheduleTypeTranslations = { [key in ScheduleTypeName]?: string };

export const SelectScheduleType = ({
  scheduleType,
  setScheduleType,
  helpContent,
  scheduleTypeTranslations,
  allowedScheduleTypes,
  disabled = false,
}: ScheduleTypeProps) => {
  const { selectedType, data } = scheduleType;
  const { t } = useTranslation();

  const selectType = (option: { value: string; label: string }) => {
    const selectedType = option.value as ScheduleTypeName;
    setScheduleType({ ...scheduleType, selectedType });
  };

  const setDailySchedule = (dailyScheduleType: DailyScheduleType) => {
    const nextScheduleType = { ...scheduleType };
    nextScheduleType.data[ScheduleTypeName.DAILY] = dailyScheduleType;
    setScheduleType(nextScheduleType);
  };

  const setWeeklySchedule = (daysOfTheWeek: WeeklyScheduleData) => {
    const nextScheduleType = { ...scheduleType };
    nextScheduleType.data[ScheduleTypeName.WEEKLY] = daysOfTheWeek;
    setScheduleType(nextScheduleType);
  };

  const setMonthlySchedule = (monthlySchedule: MonthlyScheduleData) => {
    const nextScheduleType = { ...scheduleType };
    nextScheduleType.data[ScheduleTypeName.MONTHLY] = monthlySchedule;
    setScheduleType(nextScheduleType);
  };

  const options = _.chain(allowedScheduleTypes ?? Object.values(ScheduleTypeName))
    .map((type) => ({
      value: type,
      label: t(scheduleTypeTranslations[type] ?? TRANSLATION_FOR_SCHEDULE_TYPE[type]),
    }))
    .value();
  return (
    <div className="tw:mb-sq-15">
      <div className="flexRowContainer" data-testid="selectScheduleType">
        <div className="flexRowContainer">
          <div className="tw:items-center tw:font-bold">{t('SCHEDULE_TYPE.HOW_OFTEN')}</div>
          <div className="flexColumnContainer tw:mr-sq-5 tw:items-center">
            <div data-testid="select-schedule-type" className="flexNoShrink">
              <Select
                value={_.find(options, { value: selectedType })}
                onChange={selectType}
                options={options}
                menuPortalTarget={document.body}
                isDisabled={disabled}
              />
            </div>
            {helpContent && helpContent}
          </div>
        </div>
        <div className="flexRowContainer">
          <div className="flexColumnContainer flexNoShrink tw:grow-4">
            {selectedType === ScheduleTypeName.DAILY && (
              <DailySchedule
                selectedType={data[ScheduleTypeName.DAILY]}
                onSelect={setDailySchedule}
                disabled={disabled}
              />
            )}
            {selectedType === ScheduleTypeName.WEEKLY && (
              <WeeklySchedule days={data[ScheduleTypeName.WEEKLY]} setDays={setWeeklySchedule} disabled={disabled} />
            )}
            {selectedType === ScheduleTypeName.MONTHLY && (
              <MonthlySchedule
                monthlySchedule={data[ScheduleTypeName.MONTHLY]}
                setMonthlySchedule={setMonthlySchedule}
                disabled={disabled}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
